import React, { useState } from 'react';
import { Send, MapPin, Phone, Mail, Clock } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', mobile: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      const list = JSON.parse(localStorage.getItem('vs_contacts') || '[]');
      list.push({ ...form, ts: new Date().toISOString() });
      localStorage.setItem('vs_contacts', JSON.stringify(list));
      toast({
        title: 'Message received',
        description: 'Our team will get back to you shortly. Thank you!',
      });
      setForm({ name: '', mobile: '', email: '', message: '' });
      setSending(false);
    }, 700);
  };

  return (
    <section id="contact" className="py-16 lg:py-24 bg-[#1a1410] text-[#F7F4EA] relative overflow-hidden">
      <div className="absolute -top-32 -right-40 w-[460px] h-[460px] rounded-full bg-[#F58220]/25 blur-3xl" />
      <div className="absolute -bottom-32 -left-40 w-[460px] h-[460px] rounded-full bg-[#E9D6C1]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
        <div className="reveal">
          <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#F58220] mb-4">
            Together • Contact
          </div>
          <h2 className="h-display uppercase text-[40px] md:text-[60px] leading-[1.02]">
            Our <span className="text-[#F58220]">Professional Team</span> is here for you
          </h2>
          <p className="mt-7 text-[15px] leading-[1.85] text-[#F7F4EA]/75 max-w-[520px]">
            At Veetu Saama, we take pride in our dedicated and experienced team,
            always ready to support you at every step. Whether you’re choosing
            the right appliance, need expert guidance, or require after-sales
            service, our professionals are here to assist with knowledge, care,
            and efficiency.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { Icon: Phone, label: 'Call us', value: '+91 73059 70386', href: 'tel:+917305970386' },
              { Icon: Mail, label: 'Email', value: 'contact@veetusaama.com', href: 'mailto:contact@veetusaama.com' },
              { Icon: MapPin, label: 'Visit', value: 'Adyar, Chennai, Tamil Nadu', href: 'https://g.co/kgs/SG7jk9g' },
              { Icon: Clock, label: 'Hours', value: 'Mon – Sun • 10:00 AM – 9:00 PM', href: '#' },
            ].map(({ Icon, label, value, href }) => (
              <a
                key={label}
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-full border border-[#F7F4EA]/15 flex items-center justify-center group-hover:bg-[#F58220] group-hover:border-[#F58220] transition-all duration-500">
                  <Icon className="w-4 h-4 text-[#F58220] group-hover:text-white transition-colors duration-500" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#F7F4EA]/55">{label}</div>
                  <div className="text-[15px] text-[#F7F4EA]">{value}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-10 font-script text-[30px] text-[#F58220]">For a Better Tomorrow</div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="reveal bg-[#F7F4EA] text-[#1a1410] rounded-3xl p-8 md:p-10 shadow-2xl"
        >
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[#F58220] mb-2">
            Quick Info
          </div>
          <div className="h-display uppercase text-[26px] md:text-[32px] leading-tight mb-7">
            Let’s talk — we’d love to hear from you.
          </div>

          <div className="space-y-5">
            {[
              { name: 'name', label: 'Full Name', type: 'text', required: true },
              { name: 'mobile', label: 'Mobile', type: 'tel', required: true },
              { name: 'email', label: 'Email', type: 'email', required: true },
            ].map((f) => (
              <div key={f.name} className="relative">
                <input
                  id={f.name}
                  name={f.name}
                  type={f.type}
                  required={f.required}
                  value={form[f.name]}
                  onChange={handleChange}
                  placeholder=" "
                  className="peer w-full bg-transparent border-b-2 border-[#E9D6C1] focus:border-[#F58220] outline-none py-3 text-[15px] text-[#1a1410] transition-colors duration-300"
                />
                <label
                  htmlFor={f.name}
                  className="absolute left-0 top-3 text-[14px] text-[#8E8E8E] pointer-events-none transition-all duration-300 peer-focus:-top-2 peer-focus:text-[11px] peer-focus:tracking-[0.22em] peer-focus:uppercase peer-focus:font-semibold peer-focus:text-[#F58220] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:tracking-[0.22em] peer-[:not(:placeholder-shown)]:uppercase peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-[#4D4D4D]"
                >
                  {f.label}
                </label>
              </div>
            ))}
            <div className="relative">
              <textarea
                id="message"
                name="message"
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder=" "
                className="peer w-full bg-transparent border-b-2 border-[#E9D6C1] focus:border-[#F58220] outline-none py-3 text-[15px] text-[#1a1410] resize-none transition-colors duration-300"
              />
              <label
                htmlFor="message"
                className="absolute left-0 top-3 text-[14px] text-[#8E8E8E] pointer-events-none transition-all duration-300 peer-focus:-top-2 peer-focus:text-[11px] peer-focus:tracking-[0.22em] peer-focus:uppercase peer-focus:font-semibold peer-focus:text-[#F58220] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:tracking-[0.22em] peer-[:not(:placeholder-shown)]:uppercase peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-[#4D4D4D]"
              >
                Message
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="btn-primary mt-9 w-full md:w-auto px-9 py-3.5 rounded-full text-[11px] uppercase tracking-[0.26em] flex items-center justify-center gap-3 disabled:opacity-70"
          >
            {sending ? 'Sending…' : 'Submit'}
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
